// src/layouts/Layout.js

import React, { useContext, useEffect } from 'react';
import {
  Route,
  Switch,
  Redirect,
  MemoryRouter,
  useLocation
} from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { CloseButton, Fade } from '../components/common/Toast';

import DashboardLayout from './DashboardLayout';
import ErrorLayout from './ErrorLayout';
import AppContext from './../context/Context';
import loadable from '@loadable/component';
import IntroPage from '../hcare/components/intro/IntroPage';

const AuthBasicLayout = loadable(() => import('./AuthBasicLayout'));

// Ahora simulamos también las variantes con "/" al final
const initialEntries = [
  '/authentication/password-reset',
  '/authentication/register',
  '/authentication/loginexam',
  '/authentication/registertruck',  // índice 3
  '/guru',              // índice 4
  '/guru/',             // índice 5
  '/guru/login',        // índice 6
  '/guru/login/',       // índice 7
  '/guru/register',     // índice 8
  '/guru/forgot-password', // índice 9
  '/kiosk/login',       // índice 10
  '/kiosk/login/',      // índice 11
  '/'                   // índice 12
];

const getInitialIndex = pathname => {
  switch (pathname.toLowerCase()) {
    case '/authentication/password-reset':
      return 0;
    case '/authentication/register':
      return 1;
    case '/authentication/loginexam':
      return 2;
    case '/authentication/registertruck':
      return 3;
    case '/guru':
      return 4;
    case '/guru/':
      return 5;
    case '/guru/login':
      return 6;
    case '/guru/login/':
      return 7;
    case '/guru/register':
      return 8;
    case '/guru/forgot-password':
      return 9;
    case '/kiosk/login':
      return 10;
    case '/kiosk/login/':
      return 11;
    default:
      return 12;
  }
};

const Layout = () => {
  const { currentuser, setCurrentUser } = useContext(AppContext);
  const location = useLocation();

  useEffect(() => {
    AuthBasicLayout.preload();
  }, []);

  useEffect(() => {
    if (
      currentuser &&
      Date.now() - currentuser.startat > currentuser.jwtExpirationMs
    ) {
      setCurrentUser(null);
    }
  }, [currentuser, setCurrentUser]);

  useEffect(() => {
    const path = location.pathname.toLowerCase();
    const isGuru = path.startsWith('/guru') || path.startsWith('/kiosk');
    document.title = isGuru ? 'GURU' : 'eScoreIT';
    const favicon = document.querySelector("link[rel='shortcut icon']");
    if (favicon) {
      favicon.href = isGuru
        ? `${process.env.PUBLIC_URL}/img/guru-logo.png`
        : `${process.env.PUBLIC_URL}/favicon.ico`;
    }
  }, [location.pathname]);

  return (
    <MemoryRouter
      initialEntries={initialEntries}
      initialIndex={getInitialIndex(location.pathname)}
    >
      <Switch>
        {/* 0) Error pages */}
        <Route path="/errors" component={ErrorLayout} />

        {/* 1) Auth routes */}
        <Route path="/authentication" component={AuthBasicLayout} />

        {/* 1b) Intro page - rendered before DashboardLayout to avoid flash */}
        <Route
          path="/intro"
          render={() =>
            currentuser ? <IntroPage /> : <Redirect to="/authentication/login" />
          }
        />

        {/* 2) /guru, /guru/, /guru/login, /guru/login/ — always show the auth layout,
             regardless of session, matching /authentication's ungated behavior above.
             Login itself explicitly navigates away (see LoginForm.js) so this doesn't
             affect the post-login redirect. */}
        <Route
          exact
          path={['/guru', '/guru/', '/guru/login', '/guru/login/']}
          render={props => <AuthBasicLayout guru={true} {...props} />}
        />

        {/* 2b) /kiosk/login — return login for kiosk users after logout */}
        <Route
          exact
          path={['/kiosk/login', '/kiosk/login/']}
          render={props =>
            currentuser === null ? (
              <AuthBasicLayout kiosk={true} {...props} />
            ) : (
              <DashboardLayout {...props} />
            )
          }
        />

        {/* 3) Other /guru/* (register, forgot-password, ...) — same ungated behavior as above */}
        <Route
          path="/guru"
          render={props => <AuthBasicLayout guru={true} {...props} />}
        />

        {/* 4) Root */}
        <Route
          path="/"
          render={props =>
            currentuser === null ? (
              <Redirect to="/authentication/login" />
            ) : (
              <DashboardLayout {...props} />
            )
          }
        />
      </Switch>

      <ToastContainer
        transition={Fade}
        closeButton={<CloseButton />}
        position={toast.POSITION.BOTTOM_LEFT}
      />
    </MemoryRouter>
  );
};

export default Layout;
